'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import styled, { css } from 'styled-components';
import { headingColor, serifFont, sansSerifFont, h1FontFamily, h1FontSize, h1FontWeight, h1Tracking, h1LineHeight, h1IsUpperCase, h2FontFamily, h2FontSize, h2FontWeight, h2Tracking, h2LineHeight, h2IsUpperCase, h3FontFamily, h3FontSize, h3FontWeight, h3Tracking, h3LineHeight, h3IsUpperCase, h4FontFamily, h4FontSize, h4FontWeight, h4Tracking, h4LineHeight, h4IsUpperCase, h5FontFamily, h5FontSize, h5FontWeight, h5Tracking, h5LineHeight, h5IsUpperCase, h6FontFamily, h6FontSize, h6FontWeight, h6Tracking, h6LineHeight, h6IsUpperCase, token } from '@nib-components/theme';
import { color as getColor, map, validFgColorValues, breakpoint } from '@nib/layout';
export const validComponentValues = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'label', 'span', 'header', 'p'];
export const validFontTypeValues = ['serif', 'sans-serif'];
export const validAlignValues = ['left', 'center', 'right', 'start', 'end'];
export const validSizeValues = [1, 2, 3, 4, 5, 6]; // Allowed values for sizes
const validBreakpointValues = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl']; // Allowed breakpoint keys
export const h1 = () => css`
  color: ${token('theme.color.fg.heading.2xl', headingColor)};
  font-family: ${token('theme.typography.heading.2xl.fontFamily', h1FontFamily)};
  font-size: ${token('theme.typography.heading.2xl.fontSize', h1FontSize)};
  font-weight: ${token('theme.typography.heading.2xl.fontWeight', h1FontWeight)};
  letter-spacing: ${token('theme.typography.heading.2xl.letterSpacing', h1Tracking)};
  line-height: ${token('theme.typography.heading.2xl.lineHeight', h1LineHeight)};
  ${props => h1IsUpperCase(props) && 'text-transform: uppercase;'};
  text-transform: ${token('theme.typography.heading.2xl.textTransform')};
`;
export const h2 = () => css`
  color: ${token('theme.color.fg.heading.xl', headingColor)};
  font-family: ${token('theme.typography.heading.xl.fontFamily', h2FontFamily)};
  font-size: ${token('theme.typography.heading.xl.fontSize', h2FontSize)};
  font-weight: ${token('theme.typography.heading.xl.fontWeight', h2FontWeight)};
  letter-spacing: ${token('theme.typography.heading.xl.letterSpacing', h2Tracking)};
  line-height: ${token('theme.typography.heading.xl.lineHeight', h2LineHeight)};
  ${props => h2IsUpperCase(props) && 'text-transform: uppercase;'};
  text-transform: ${token('theme.typography.heading.xl.textTransform')};
`;
const h3 = () => css`
  color: ${token('theme.color.fg.heading.lg', headingColor)};
  font-family: ${token('theme.typography.heading.lg.fontFamily', h3FontFamily)};
  font-size: ${token('theme.typography.heading.lg.fontSize', h3FontSize)};
  font-weight: ${token('theme.typography.heading.lg.fontWeight', h3FontWeight)};
  letter-spacing: ${token('theme.typography.heading.lg.letterSpacing', h3Tracking)};
  line-height: ${token('theme.typography.heading.lg.lineHeight', h3LineHeight)};
  ${props => h3IsUpperCase(props) && 'text-transform: uppercase;'};
  text-transform: ${token('theme.typography.heading.lg.textTransform')};
`;
const h4 = () => css`
  color: ${token('theme.color.fg.heading.md', headingColor)};
  font-family: ${token('theme.typography.heading.md.fontFamily', h4FontFamily)};
  font-size: ${token('theme.typography.heading.md.fontSize', h4FontSize)};
  font-weight: ${token('theme.typography.heading.md.fontWeight', h4FontWeight)};
  letter-spacing: ${token('theme.typography.heading.md.letterSpacing', h4Tracking)};
  line-height: ${token('theme.typography.heading.md.lineHeight', h4LineHeight)};
  ${props => h4IsUpperCase(props) && 'text-transform: uppercase;'};
  text-transform: ${token('theme.typography.heading.md.textTransform')};
`;
const h5 = () => css`
  color: ${token('theme.color.fg.heading.sm', headingColor)};
  font-family: ${token('theme.typography.heading.sm.fontFamily', h5FontFamily)};
  font-size: ${token('theme.typography.heading.sm.fontSize', h5FontSize)};
  font-weight: ${token('theme.typography.heading.sm.fontWeight', h5FontWeight)};
  letter-spacing: ${token('theme.typography.heading.sm.letterSpacing', h5Tracking)};
  line-height: ${token('theme.typography.heading.sm.lineHeight', h5LineHeight)};
  ${props => h5IsUpperCase(props) && 'text-transform: uppercase;'};
  text-transform: ${token('theme.typography.heading.sm.textTransform')};
`;
const h6 = () => css`
  color: ${token('theme.color.fg.heading.xs', headingColor)};
  font-family: ${token('theme.typography.heading.xs.fontFamily', h6FontFamily)};
  font-size: ${token('theme.typography.heading.xs.fontSize', h6FontSize)};
  font-weight: ${token('theme.typography.heading.xs.fontWeight', h6FontWeight)};
  letter-spacing: ${token('theme.typography.heading.xs.letterSpacing', h6Tracking)};
  line-height: ${token('theme.typography.heading.xs.lineHeight', h6LineHeight)};
  ${props => h6IsUpperCase(props) && 'text-transform: uppercase;'};
  text-transform: ${token('theme.typography.heading.xs.textTransform')};
`;
export const BaseHeadingComponent = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    align,
    size,
    component,
    fontType,
    color,
    children,
    ...otherProps
  } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let Component = component;
  // if size is a number, we can infer the component
  if (!Component && typeof size === 'number') {
    Component = `h${size}`;
  }
  if (process.env.NODE_ENV !== 'production') {
    const invariant = require('invariant');
    invariant(Component || typeof size === 'number', '@nib-components/heading: Must have component prop set if size prop is an object.');
  }
  return /*#__PURE__*/_jsx(Component, {
    ...otherProps,
    children: children
  });
};
const isNamedColor = color => {
  if (!color) return false;
  if (validFgColorValues.includes(color)) {
    return true;
  }
  return false;
};
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const setColor = props => {
  if (isNamedColor(props.color)) {
    return getColor();
  }
  // Else return the old color behavior (selector or raw string)
  return css`
    color: ${props.color};
  `;
};
const Heading = styled(BaseHeadingComponent).attrs({
  'data-mesh-component': 'HEADING'
})`
  margin: 0;
  text-wrap: balance;
  word-break: break-word;
  hyphens: auto;

  ${breakpoint('mini')`
    hyphens: none;  
  `}

  ${_ref => {
  let {
    size
  } = _ref;
  return size && map(size, val => {
    if (val === 1) return h1();
    if (val === 2) return h2();
    if (val === 3) return h3();
    if (val === 4) return h4();
    if (val === 5) return h5();
    if (val === 6) return h6();
  });
}};

  &&& {
    ${props => setColor({
  ...props,
  color: props.color
})};
  }

  ${_ref2 => {
  let {
    align
  } = _ref2;
  return map(align, val => {
    if (align) {
      return css`
          text-align: ${val === 'start' || val === 'left' ? 'start' : val === 'end' || val === 'right' ? 'end' : val};
        `;
    }
    return '';
  });
}}

  ${_ref3 => {
  let {
    theme,
    fontType
  } = _ref3;
  return fontType ? theme.id === 'nib' ? fontType === 'sans-serif' ? css`
              &&& {
                font-family: ${sansSerifFont};
                font-weight: 700;
              }
            ` : css`
              &&& {
                font-family: ${serifFont};
                font-weight: 600;
              }
            ` : `` : ``;
}};
`;
Heading.displayName = 'Heading';
export default Heading;